import React, { forwardRef, memo, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Box, InputAdornment } from "@mui/material";
import IpdTextField from "./IpdTextField";
import {
  dateToIsoDateTime,
  formatDisplayInputWhileTyping,
  isoToDate,
  isoToDateTime,
  parseDisplayToIso,
  normalizeToIsoDate,
} from "./dateFormat";
import "react-datepicker/dist/react-datepicker.css";
import "./ipdDateField.css";

const PickerInput = forwardRef(({
  value: externalValue,
  onClick,
  onChange,
  onBlur,
  disabled,
  error,
  placeholder,
  formatOnType = true,
  ...rest
}, ref) => {
  const [localValue, setLocalValue] = useState(externalValue || "");
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setLocalValue(externalValue || "");
    }
  }, [externalValue]);

  const handleChange = (e) => {
    const next = formatOnType
      ? formatDisplayInputWhileTyping(e.target.value)
      : e.target.value;
    setLocalValue(next);
    onChange?.({
      ...e,
      target: { ...e.target, value: next },
    });
  };

  const handleFocus = () => {
    focusedRef.current = true;
  };

  const handleBlur = (e) => {
    focusedRef.current = false;
    onBlur?.(e);
  };

  return (
    <IpdTextField
      {...rest}
      inputRef={ref}
      fullWidth
      size="small"
      value={localValue}
      placeholder={placeholder || (formatOnType ? "dd/mm/yyyy" : "dd/mm/yyyy hh:mm")}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      error={error}
      inputProps={formatOnType ? { inputMode: "numeric" } : undefined}
      InputProps={{
        endAdornment: (
          <InputAdornment
            position="end"
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              onClick?.(e);
            }}
            onMouseDown={(e) => {
              if (disabled) return;
              e.preventDefault();
            }}
            sx={{ cursor: disabled ? "default" : "pointer", color: "action.active", mr: 0.25 }}
          >
            <CalendarTodayIcon sx={{ fontSize: 18 }} />
          </InputAdornment>
        ),
      }}
    />
  );
});

PickerInput.displayName = "IpdDatePickerInput";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getYearRange(minDate, maxDate) {
  const now = new Date();
  const startYear = minDate?.getFullYear() ?? now.getFullYear() - 100;
  const endYear = maxDate?.getFullYear() ?? now.getFullYear() + 20;
  const years = [];
  for (let year = endYear; year >= startYear; year -= 1) {
    years.push(year);
  }
  return years;
}

function IpdDatePickerHeader({
  date,
  changeYear,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  minDate,
  maxDate,
}) {
  const years = getYearRange(minDate, maxDate);

  return (
    <>
      <button
        type="button"
        className="react-datepicker__navigation react-datepicker__navigation--previous"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous Month"
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
          Previous Month
        </span>
      </button>
      <div className="react-datepicker__current-month">
        {MONTHS[date.getMonth()]}{" "}
        <select
          className="react-datepicker__year-select"
          value={date.getFullYear()}
          onChange={(e) => changeYear(Number(e.target.value))}
          aria-label="Select year"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className="react-datepicker__navigation react-datepicker__navigation--next"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next Month"
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
          Next Month
        </span>
      </button>
    </>
  );
}

/**
 * SILENT CALENDAR BUG — read before changing date handling on any screen.
 *
 * react-datepicker `minDate` / `maxDate` only grey out days in the popup. They
 * do NOT block: open calendar → backspace-clear → type dd/mm/yyyy on the
 * keyboard.
 *
 * Worse: on typing, react-datepicker often passes a Date built from the
 * previously `selected` value (e.g. year 2020) while the input still shows what
 * the user typed (e.g. 15/09/2026). If we trust that Date, form state silently
 * becomes 15/09/2020 — hospital staff think they entered 2026, click Next, and
 * the year mutates with no error. That is unacceptable.
 *
 * Rules:
 * 1. On input/change/blur, ALWAYS parse the typed dd/mm/yyyy text — never the
 *    Date guess from react-datepicker.
 * 2. Use the Date argument only for calendar day clicks (no typed input event).
 * 3. Always emit yyyy-MM-dd (or ""). Out-of-range dates stay as ISO so the
 *    screen can show "not allowed" — never rewrite the user's year/day.
 */
function IpdDateField({
  value = "",
  onChange,
  disabled = false,
  error = false,
  placeholder,
  maxDate,
  minDate,
  fullWidth = false,
  showTime = false,
  ...rest
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const emitIso = (raw) => {
    const iso = normalizeToIsoDate(raw);
    onChange?.(iso);
  };

  const commitTypedDisplay = (rawDisplay, eventType) => {
    const formatted = formatDisplayInputWhileTyping(rawDisplay);
    const parsed = parseDisplayToIso(formatted);

    if (parsed) {
      emitIso(parsed);
      return;
    }

    if (!formatted.trim()) {
      onChange?.("");
      return;
    }

    // Incomplete / unparseable on blur: snap UI back to last committed ISO.
    // Do NOT invent another year from the open calendar month.
    if (eventType === "blur") {
      emitIso(value);
    }
  };

  const handleCalendarChange = (date) => {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      if (showTime) {
        onChange?.(dateToIsoDateTime(date));
        return;
      }
      emitIso(date);
      return;
    }
    onChange?.("");
  };

  const handleChangeRaw = (event) => {
    if (showTime) return;
    const eventType = event?.type;
    if (eventType !== "change" && eventType !== "input" && eventType !== "blur") {
      return;
    }
    // PickerInput spreads the React event ({...e}), which does NOT copy
    // preventDefault — so it may be missing. Only call when it is a real fn.
    // Goal: stop react-datepicker from also emitting a Date with the old year.
    if (typeof event?.preventDefault === "function") {
      event.preventDefault();
    }
    commitTypedDisplay(event?.target?.value ?? "", eventType);
  };

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: calendarOpen ? 30 : "auto",
      }}
    >
      <DatePicker
        selected={showTime ? isoToDateTime(value) : isoToDate(value)}
        onChange={handleCalendarChange}
        onChangeRaw={handleChangeRaw}
        dateFormat={showTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
        timeFormat="HH:mm"
        timeIntervals={15}
        timeCaption="Time"
        showTimeSelect={showTime}
        placeholderText={placeholder || (showTime ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy")}
        disabled={disabled}
        maxDate={maxDate}
        minDate={minDate}
        filterTime={showTime && minDate
          ? (time) => time.getTime() >= minDate.getTime()
          : undefined}
        showPopperArrow={false}
        wrapperClassName={fullWidth ? "ipd-datepicker-input-wrapper" : undefined}
        renderCustomHeader={(headerProps) => (
          <IpdDatePickerHeader
            {...headerProps}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
        preventOpenOnFocus
        popperPlacement="bottom-start"
        popperClassName="ipd-datepicker-popper"
        calendarClassName="ipd-datepicker-calendar"
        popperProps={{
          strategy: "fixed",
          modifiers: [
            {
              name: "zIndex",
              enabled: true,
              phase: "write",
              fn({ state }) {
                state.styles.popper.zIndex = 1600;
              },
            },
          ],
        }}
        onCalendarOpen={() => setCalendarOpen(true)}
        onCalendarClose={() => setCalendarOpen(false)}
        customInput={(
          <PickerInput
            error={error}
            placeholder={placeholder || (showTime ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy")}
            formatOnType={!showTime}
            {...rest}
          />
        )}
      />
    </Box>
  );
}

export default memo(IpdDateField);
